import { Link } from "react-router-dom";
import { Mail, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { InteractiveBackground } from "@/components/InteractiveBackground";
import Lottie from "lottie-react";
import holdingCookieAnimation from "@/assets/holding-cookie.json";
export const Hero = () => {
  const { t } = useLanguage();
  const { visibleItems, itemRefs } = useScrollAnimation(0.1);
  const scrollToProjects = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      const yOffset = -80; // Offset for fixed header
      const targetY = projectsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.scrollTo(0, targetY);
        return;
      }
      const start = window.scrollY;
      const duration = 2000; // 2 seconds for the scroll
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth deceleration
        const easeInOutCubic = (t: number) => 
          t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        
        const easedProgress = easeInOutCubic(progress);
        const currentPosition = start + (targetY - start) * easedProgress;
        window.scrollTo(0, currentPosition);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  };
  return <section className="relative min-h-screen flex flex-col justify-between overflow-hidden pb-12 md:pb-0">
      {/* Interactive Background */}
      <InteractiveBackground />

      {/* Cookie Animation at Top Center */}
      <div
        aria-hidden="true"
        ref={(el) => (itemRefs.current[0] = el)}
        className={`absolute -top-12 md:-top-16 left-1/2 -translate-x-1/2 z-10 w-48 md:w-64 scale-y-[-1] transition-all duration-700 ${
          visibleItems.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
        }`}
      >
        <Lottie animationData={holdingCookieAnimation} loop={true} />
      </div>

      {/* Background overlay - reduced opacity to show particles */}
      <div className="absolute inset-0 z-[2] bg-background/50" />


      {/* Content - Main Section */}
      <div className="relative z-20 max-w-[1300px] mx-auto px-6 pt-[200px] pb-32 text-left flex-1 flex items-start">
        <div className="animate-fade-in">
          <p className="text-sm md:text-base uppercase tracking-widest text-primary mb-4 font-medium">{t("hero.eyebrow")}</p>
          <h1 className="text-[40px] md:text-[64px] font-normal mb-8 leading-[1.05] tracking-tight text-foreground">
            {t("hero.headline")}
          </h1>
          
          {/* Social Icons */}
          <div className="flex flex-col items-center md:items-start gap-6 mb-8">
            <div className="w-48 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <span className="font-mono text-2xl text-primary">{t("hero.sayHello")}</span>
              
              <div className="h-px md:h-8 w-12 md:w-px bg-border" />
              
              <div className="flex flex-row items-center gap-6">
                <a href="https://www.linkedin.com/in/khaledkasmou/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </a>
                
                <div className="h-8 w-px bg-border hidden md:block" />
                
                <a href="mailto:khaledkasmou@gmail.com" className="text-foreground hover:text-primary transition-colors" aria-label="Email">
                  <Mail className="w-5 h-5" />
                </a>
                
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 justify-center md:justify-start">
            <Button size="lg" variant="premium" onClick={scrollToProjects}>
              {t("nav.work")}
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/portfolio-review" className="group/cta inline-flex items-center gap-2">
                {t("nav.portfolioReview")}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-1 motion-reduce:transition-none" aria-hidden="true" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">{t("nav.contact")}</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Professional Info - Bottom of Viewport */}
      <div className="relative z-20 max-w-[1300px] mx-auto px-6 pb-10 w-full">
        <div className="pb-4 relative w-full">
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
            {t("hero.currentlyAt")} <a href="https://www.meetotis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">OtisAI</a> {t("hero.previouslyAt")} <a href="https://www.kasha.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">Kasha.io</a>.
            <br />
            {t("hero.basedIn")} <span className="text-foreground font-medium">{t("hero.sofia")}</span>
          </p>
          {/* Gradient border */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>
      </div>

      {/* Static gradient at bottom - no animation */}
      <div className="absolute bottom-0 left-0 right-0 h-48 overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0 bg-gradient-to-t from-blue-400/20 via-blue-300/10 to-transparent" />
        </div>
        {/* Fade to background */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>
    </section>;
};